/**
 * /me/data — Download my data (GDPR data portability, Story D3).
 *
 * Renders a "Download my data" button. On click, fetches POST /api/me/export,
 * creates a Blob, and triggers a download as a JSON file.
 */

import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shell } from '@/components/shell';
import { Button } from '@/components/ui';
import { apiFetch } from '@/lib/api-client';

type FetchState = 'idle' | 'loading' | 'success' | 'error';

export function MyDataPage(): ReactElement {
  const navigate = useNavigate();
  const [state, setState] = useState<FetchState>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function handleDownload(): Promise<void> {
    setState('loading');
    setErrorMsg('');
    try {
      const res = await apiFetch('/api/me/export', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/sign-in?return_to=' + encodeURIComponent(window.location.pathname));
        return;
      }
      if (!res.ok) {
        const body = (await res.json()) as { error?: { message?: string } };
        setErrorMsg(body.error?.message ?? 'Export failed');
        setState('error');
        return;
      }
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bimassess-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setState('success');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Network error');
      setState('error');
    }
  }

  return (
    <Shell mode='individual' densityMode='editorial'>
      <p className="font-mono text-mono text-muted-2">MY DATA</p>
      <h1 className="mt-2 font-sans text-headline font-medium tracking-tight">Download my data</h1>

      <p className="mt-4 text-muted">
        Download a copy of all your data stored on BIMAssess, including your
        profile, attempt history, and answers. The export is provided in JSON
        format for data portability (GDPR Article 20).
      </p>

      <Button
        type="button"
        variant='primary'
        className="mt-6"
        disabled={state === 'loading'}
        onClick={() => void handleDownload()}
      >
        {state === 'loading' ? 'Preparing export…' : 'Download my data'}
      </Button>

      {state === 'success' && (
        <p className="mt-4 text-sm text-success">Export downloaded successfully.</p>
      )}

      {state === 'error' && (
        <p className="mt-4 text-sm text-error">{errorMsg}</p>
      )}
    </Shell>
  );
}